import * as Sentry from '@sentry/browser'
import _get from 'lodash/get'

import config from '../config'

export const initSentry = () => {
    Sentry.init({
        dsn: config.isProduction || config.isStaging || config.sentryDevelopment ? config.sentryDsn : null,
        environment: config.environment,
        beforeBreadcrumb(breadcrumb, hint) {
            let { message } = breadcrumb
            if (breadcrumb.category === 'ui.click') {
                const { target } = hint.event
                if (target.textContent) {
                    message = `${message} ("${target.textContent.substring(0, 20)}${
                        target.textContent.length > 20 ? '...' : ''
                    }")`
                }
            }
            return { ...breadcrumb, message }
        },
    })
}

export const initSentryUser = (profile) => {
    Sentry.getCurrentScope().setUser({
        id: profile.id,
        variant: profile.variant,
    })
}

export const sendApiErrorsToSentry = (exception) => {
    Sentry.withScope((scope) => {
        scope.setContext('apiCall', {
            requestData: JSON.stringify(_get(exception, ['response', 'config', 'data'], {})),
            responseData: JSON.stringify(_get(exception, ['response', 'data'], {})),
            sentryXhr: JSON.stringify(_get(exception, ['response', 'request'], {})),
        })

        // Try to parse more info from Axios. If fails, report as exception.
        try {
            const apiStatus = _get(exception, ['response', 'status'], '')
            const apiMethod = _get(exception, ['response', 'config', 'method'], '')
            const apiEndpoint = formatApiEndpoint(_get(exception, ['request', 'responseURL'], ''))

            Sentry.captureMessage(`${apiMethod.toUpperCase()} ${apiEndpoint} [${apiStatus}]`, {
                level: 'info', // https://github.com/getsentry/sentry-javascript/blob/7.0.0/MIGRATION.md#severity-severitylevel-and-severitylevels
                fingerprint: ['{{ default }}', String(apiStatus), String(apiMethod), String(apiEndpoint)],
            })
        } catch {
            Sentry.captureException(exception)
        }
    })
}

export const sendDecodingErrorsToSentry = (exception, string, componentName = 'a component') => {
    Sentry.captureMessage(`Decoding failed in ${componentName}`, {
        contexts: {
            decoding: { value: string, componentName, exception },
        },
        fingerprint: ['{{ default }}', 'DecodingError', componentName],
    })
}

export const reportFileUploadError = (exception, file) => {
    const fileName = file?.name || 'unknown'
    const fileExtension = fileName.split('.').slice(-1)[0]
    const errorMessage = exception?.response?.data?.message || ''

    if (exception?.response?.status === 401) return

    Sentry.captureMessage(`File upload failed`, {
        contexts: {
            error: { message: errorMessage, fileExtension },
        },
        tags: {
            'fileUploadError.fileExtension': fileExtension === fileName ? 'N/A' : fileExtension,
            'fileUploadError.errorMessage': errorMessage,
        },
        fingerprint: ['{{ default }}', 'FileUploadError'],
    })
}

export const reportNylasBookingToSentry = (exception, data) => {
    Sentry.captureMessage(`Failed to update Nylas booking`, {
        contexts: { exception, data },
        fingerprint: ['{{ default }}', 'NylasBookingUpdateError'],
    })
}

export const reportNylasMissingConfigurations = (exception, data) => {
    Sentry.captureMessage(`Missing Nylas configurations to open calendar`, {
        contexts: { exception, data },
        fingerprint: ['{{ default }}', 'NylasMissingConfigurations'],
    })
}

export const reportNylasSchedulerError = (exception, data) => {
    Sentry.captureMessage(`Nylas scheduler error`, {
        contexts: { exception, data },
        fingerprint: ['{{ default }}', 'NylasSchedulerError'],
    })
}

/**
 * @param {Object} args
 * @param {string} args.message - Message to be sent to Sentry
 * @param {Object} [args.extra={}] - Extra context to be sent to Sentry
 * @param {string[]} [args.fingerprint=[]] - Fingerprint array to customize grouping in Sentry
 */
export const captureSentryMessage = ({ message, extra = {}, fingerprint = [] }) => {
    Sentry.captureMessage(message, {
        fingerprint: ['{{ default }}', ...fingerprint],
        extra,
    })
}

const regexDigits = /\d+/g
const regexUUID = /^[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}$/i
const cleanPathnames = ['/api/payment/paymentconfirmation', '/api/magiclink', '/v1/postcodes']
const formatApiEndpoint = (urlString) => {
    if (!urlString) return urlString

    const { pathname: apiEndpoint } = new URL(urlString)

    const newPathnameIndex = cleanPathnames.findIndex((cleanerPathname) => apiEndpoint.includes(cleanerPathname))
    if (newPathnameIndex >= 0) return cleanPathnames[newPathnameIndex]

    return apiEndpoint
        .split('/')
        .map((pathnamePart) => pathnamePart.replace(regexUUID, '{uuid}').replace(regexDigits, '{id}'))
        .join('/')
}
